import { Textarea as GelTextarea } from '@snsw/react-component-library';
import {
  ChangeEvent,
  DetailedHTMLProps,
  forwardRef,
  TextareaHTMLAttributes,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { CharacterCounter } from './CharacterCounter';

export type TextareaProps = Omit<
  DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
  'rows' | 'value' | 'defaultValue' | 'maxLength'
> & {
  value?: string;
  defaultValue?: string;
  rows?: 'small' | 'medium' | 'large';
  inputWidth?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hasError?: boolean;

  /**
   * The maximum number of characters allowed in the text field. Note that the text field will show a character counter
   * if this value is set to anything greater than 0. Defaults to 4,000.
   */
  maxLength?: number | null;

  /**
   * Set to `true` to disable showing the character counter below the component, even if a `maxLength` has been set.
   */
  hideCharacterCounter?: boolean;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>((props, forwardedRef) => {
  const { maxLength, onChange, value, defaultValue, hideCharacterCounter } = props;

  const inputRef = useRef<HTMLTextAreaElement>();
  useImperativeHandle(forwardedRef, () => inputRef.current);

  // We have to mimic the max-width style attribute from Kiama's text area to cater for the input area being
  // rendered less than 100% width.
  const [maxWidth, setMaxWidth] = useState<string | undefined>();

  useLayoutEffect(() => {
    if (inputRef.current instanceof Element) {
      setMaxWidth(getComputedStyle(inputRef.current).getPropertyValue('max-width'));
    }
  }, []);

  const [uncontrolledCharCount, setUncontrolledCharCount] = useState(() => getCharCount(defaultValue));

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event);

    // To save a superfluous re-render, don't bother changing state if we're not showing the character counter.
    if (!hideCharacterCounter) {
      setUncontrolledCharCount(event.target.value.length);
    }
  };

  // Favour the `value` length for a controlled component versus `uncontrolledCharCount` which is only needed for
  // an uncontrolled component.
  const charCount = getCharCount(value) ?? uncontrolledCharCount ?? 0;

  return (
    <>
      <GelTextarea
        {...props}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        inputRef={inputRef}
      />

      {!hideCharacterCounter && <CharacterCounter current={charCount} limit={maxLength} maxWidth={maxWidth} />}
    </>
  );
});

Textarea.defaultProps = {
  maxLength: 4_000,
};

function getCharCount(value?: string) {
  return typeof value === 'string' ? value.length : null;
}
